import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// MUI components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 PRO React TS examples components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
    navbar,
    navbarContainer,
    navbarRow,
    navbarIconButton,
    navbarDesktopMenu,
    navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 PRO React context
import {
    useMaterialUIController,
    setTransparentNavbar,
    setMiniSidenav,
    setOpenConfigurator,
} from "context";

import useLogout from "hooks/useLogout";

// Declaring prop types for DashboardNavbar
interface Props {
    absolute?: boolean;
    light?: boolean;
    isMini?: boolean;
}

function TopNavbar({ absolute, light, isMini }: Props): JSX.Element {
    const logout = useLogout();
    const navigate = useNavigate();

    const handleLogout = (event?: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        if (event) event.preventDefault();
        logout();
        navigate("/authentication/sign-in");
    };

    const [navbarType, setNavbarType] = useState<
        "fixed" | "absolute" | "relative" | "static" | "sticky"
    >();
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
    const [openMenu, setOpenMenu] = useState<null | HTMLElement>(null);
    const route = useLocation().pathname.split("/").slice(1);

    useEffect(() => {
        setNavbarType(fixedNavbar ? "sticky" : "static");

        function handleTransparentNavbar() {
            setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
        }
        window.addEventListener("scroll", handleTransparentNavbar);
        handleTransparentNavbar();
        return () => window.removeEventListener("scroll", handleTransparentNavbar);
    }, [dispatch, fixedNavbar]);

    const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
    const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => setOpenMenu(event.currentTarget);
    const handleCloseMenu = () => setOpenMenu(null);

    // Render the logout menu
    const renderMenu = () => (
        <Menu
            anchorEl={openMenu}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
            open={Boolean(openMenu)}
            onClose={handleCloseMenu}
            sx={{ mt: 2 }}
        >
            <MenuItem
                onClick={() => {
                    handleLogout();
                    handleCloseMenu();
                }}
            >
                Logout
            </MenuItem>
        </Menu>
    );

    // Styles for the navbar icons
    const iconsStyle = ({
                            palette: { dark, white, text },
                            functions: { rgba },
                        }: {
        palette: any;
        functions: any;
    }) => ({
        color: () => {
            let colorValue = light || darkMode ? white.main : dark.main;
            if (transparentNavbar && !light) {
                colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
            }
            return colorValue;
        },
    });

    return (
        <AppBar
            position={absolute ? "absolute" : navbarType}
            color="inherit"
            sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
        >
            <Toolbar
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    minHeight: "64px",
                }}
            >
                {/* Left: Menu Button */}
                <MDBox display="flex" alignItems="center">
                    <IconButton
                        sx={{
                            ...navbarDesktopMenu,
                            pl: 0, // remove padding if needed
                        }}
                        onClick={handleMiniSidenav}
                        size="small"
                        disableRipple
                    >
                        <Icon fontSize="medium" sx={iconsStyle}>
                            {miniSidenav ? "menu_open" : "menu"}
                        </Icon>
                    </IconButton>
                </MDBox>

                {/* Right: All other buttons */}
                {!isMini && (
                    <MDBox display="flex" alignItems="center" ml="auto">

                        <IconButton sx={navbarIconButton} size="small" disableRipple onClick={handleOpenMenu}>
                            <Icon sx={iconsStyle}>account_circle</Icon>
                        </IconButton>
                        {/*<IconButton
                            size="small"
                            disableRipple
                            color="inherit"
                            sx={navbarIconButton}
                            onClick={handleConfiguratorOpen}
                        >
                            <Icon sx={iconsStyle}>settings</Icon>
                        </IconButton>*/}
                        {renderMenu()}
                    </MDBox>
                )}
            </Toolbar>

        </AppBar>
    );
}

TopNavbar.defaultProps = {
    absolute: false,
    light: false,
    isMini: false,
};

export default TopNavbar;
